import { Injectable } from '@angular/core';
import { Router, Route } from '@angular/router';
import { RuntimeConfigService } from './runtime-config.service';
import { IRouteConfig, IRouteDetails } from '../../models/config.models';
import { inject } from '@angular/core';
import { ReportViewComponent } from '../../report-view/report-view.component';
import { RedirectComponent } from '../../../external-link/redirect/redirect.component';
import { LayoutComponent } from '../../../_layout/layout.component';

@Injectable({ providedIn: 'root' })
export class DynamicRoutingService {
  constructor(private router: Router, private runtimeConfig: RuntimeConfigService) {
    console.log('DynamicRoutingService constructor called');
  }

  /**
   * Builds and registers dynamic routes based on API-driven config
   */
  public setupRoutes(): Promise<void> {
    console.log('DynamicRoutingService.setupRoutes() called');

    return new Promise((resolve) => {
      const clientConfig = this.runtimeConfig.routingConfig;
      if (!clientConfig?.length) {
        console.warn('No routing config found, skipping dynamic route setup');
        resolve();
        return;
      }

      console.log('Setting up dynamic routes with config:', clientConfig);
      const dynamicChildRoutes: Route[] = [];

      clientConfig.forEach((cfg: IRouteConfig) => {
        // Only process routes that should be dynamic (not static like management, resources, etc.)
        if (cfg.children?.length && !cfg.isStatic) {
          // console.log('Processing dynamic route for category:', cfg.category);
          const children: Route[] = cfg.children
            .map(child => this.mapDetailToRoute(child))
            .filter(route => route !== null); // Filter out null routes

          if (children.length > 0) {
            // Create a parent route for this category (e.g., 'leads', 'sales', etc.)
            dynamicChildRoutes.push({
              path: cfg.category,
              children,
              data: { id: cfg.id, title: cfg.displayName, breadcrumb: cfg.displayName, roles: cfg.roles }
            });
          }
        }
      });

      // console.log('Generated dynamic child routes:', dynamicChildRoutes);

      // Get the original router configuration
      const currentConfig = this.router.config;

      // Find the root route (path: '')
      const rootRouteIndex = currentConfig.findIndex(route => route.path === '');

      if (rootRouteIndex === -1) {
        console.error('Could not find root route with empty path');
        resolve();
        return;
      }
      

      // Create the default redirect route
      const defaultRedirectRoute: Route = {
        path: '',
        redirectTo: this.runtimeConfig.config.defaultRoute, // this defaults to /leads/overview in the api
        pathMatch: 'full'
      };

      // Update the root route's children to include the dynamic routes
      // But preserve all other static routes (management, resources, etc.)
      const updatedConfig = [...currentConfig];
      updatedConfig[rootRouteIndex] = {
        ...currentConfig[rootRouteIndex],
        children: [defaultRedirectRoute, ...dynamicChildRoutes]
      };

      console.log('Final router config:', updatedConfig);
      console.log('Static routes from config:', clientConfig.filter(cfg => cfg.isStatic).map(cfg => cfg.category));
      console.log('Static routes preserved in router:', updatedConfig.filter(route => route.path !== '' && route.component === LayoutComponent).map(route => route.path));
      this.router.resetConfig(updatedConfig);
      resolve();
    });
  }

  private mapDetailToRoute(detail: IRouteDetails): Route | null {
    // Determine which component to use based on route configuration
    let component: any;
    let routeData: any = {
      breadcrumb: detail.displayName,
      ...detail.routeData
    };

    if (detail.reportName) {
      // This is a report route - use ReportViewComponent
      component = ReportViewComponent;
      routeData.reportName = detail.reportName;
    } else if (detail.routeData?.app || detail.routeData?.externalUrl) {
      // This is an external link route - use RedirectComponent
      component = RedirectComponent;
    } else if (detail.routeData?.loadChildren) {
      // This is a lazy-loaded Angular module - return a route with loadChildren
      return {
        path: detail.route,
        loadChildren: detail.routeData.loadChildren,
        data: routeData
      };
    } else if (detail.routeData?.component) {
      // This is a specific component route - use the specified component
      // Note: The component should be imported/available in the app
      component = detail.routeData.component;
    } else {
      // Default fallback - this might be a placeholder or informational route
      // For now, we'll skip creating this route
      console.warn(`Dynamic route '${detail.route}' has no component, reportName, or routing configuration. Skipping.`);
      return null;
    }

    return {
      path: detail.route,
      component: component,
      data: routeData
    };
  }
}
